/**
 * APTable Component
 *
 * A comprehensive data table component following Figma design system specifications.
 * Provides data display with header row, data rows, sorting, and empty states.
 * Now built using modular sub-components for better maintainability.
 *
 * @example
 * ```tsx
 * <APTable
 *   columns={[
 *     { key: 'name', label: 'Name', accessor: 'name', sortable: true },
 *     { key: 'email', label: 'Email', accessor: 'email' },
 *     { key: 'status', label: 'Status', accessor: 'status', render: (value) => (
 *       <span className={`status status--${value}`}>{value}</span>
 *     )}
 *   ]}
 *   data={[
 *     { id: 1, name: 'John Doe', email: 'john@example.com', status: 'active' },
 *     { id: 2, name: 'Jane Smith', email: 'jane@example.com', status: 'inactive' }
 *   ]}
 *   sort={{ column: 'name', direction: 'asc' }}
 *   onSortChange={handleSortChange}
 * />
 * ```
 */

import { forwardRef } from 'react';

import type { IAPTableProps } from './types';

import { APTableContainer } from './components';

import './styles.scss';

/**
 * APTable - Production-ready data table component
 *
 * Now uses the modular APTableContainer internally while maintaining
 * the same API for backward compatibility.
 */
export const APTable = forwardRef<HTMLDivElement, IAPTableProps>(
  <TData,>(props: IAPTableProps<TData>, ref: React.Ref<HTMLDivElement>) => {
    return <APTableContainer ref={ref} {...props} />;
  }
) as <TData = Record<string, unknown>>(
  props: IAPTableProps<TData> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;
